import {
  ApiTags,
  GetJobResultResponse,
  JobEndpoint,
  JobStatus,
  JobsTypes,
} from "@shared/types";
import {
  JOB_TYPE_ENDPOINTS_MAPPING,
  NO_JOB_ARG_JOB_TYPES,
} from "@shared/services/jobs/constants";
import {
  EndpointBuilder,
  FetchBaseQueryError,
  ResultDescription,
  TagDescription,
} from "@reduxjs/toolkit/query";
import { sharedLib } from "@shared/services/shared.service";
import { jobsActions } from "@shared/slices/jobs.slice";

export const handleGetJobResults = async <
  E extends JobEndpoint,
  Args = JobsTypes[E]["args"],
  Response = JobsTypes[E]["response"]
>(
  endpointName: E,
  baseQuery: any,
  jobId: string,
  args: Args,
  transformResponse?: (
    rawResponse: JobsTypes[E]["rawResponse"],
    args: Args
  ) => Response,
  timeout?: number
): Promise<{ data: Response } | FetchBaseQueryError> => {
  const getJobResultsResponse:
    | { data: GetJobResultResponse }
    | FetchBaseQueryError = await baseQuery({
    url: `get_job_results/`,
    method: "POST",
    body: { job_id: jobId },
  });
  if (getJobResultsResponse && "error" in getJobResultsResponse) {
    const { dispatch } = sharedLib.store;
    const errorData = (getJobResultsResponse.error as any)?.data;
    const errorMessage: string | undefined = errorData?.message ?? undefined;
    dispatch(
      jobsActions.setJobData({
        args,
        data: null,
        endpointName,
        isError: true,
        errorMessage,
      })
    );
    return {
      error: getJobResultsResponse.error,
    } as any;
  } else {
    const jobResultsData =
      getJobResultsResponse.data as JobsTypes[E]["rawResponse"];
    const status = jobResultsData.status;
    const { dispatch } = sharedLib.store;
    let parsedData: any;
    if (endpointName === "getJobResultsJob" || status === JobStatus.FINISHED) {
      parsedData = transformResponse
        ? transformResponse(jobResultsData, args)
        : jobResultsData;
      dispatch(
        jobsActions.setJobData({ args, data: parsedData, endpointName })
      );
    } else if (status === JobStatus.IN_PROGRESS) {
      const progressData = (jobResultsData as any)?.data;
      if (progressData) {
        dispatch(
          jobsActions.setJobData({ args, data: progressData, endpointName })
        );
      }
    }
    if (status === JobStatus.IN_PROGRESS) {
      let finalTimeout = timeout ?? 300;
      if (finalTimeout < 1000) {
        finalTimeout += 200;
      }
      return new Promise(async (resolve) => {
        setTimeout(
          async () =>
            resolve(
              await handleGetJobResults(
                endpointName,
                baseQuery,
                jobId,
                args,
                transformResponse,
                finalTimeout
              )
            ),
          finalTimeout
        );
      });
    } else {
      return { data: parsedData as Response };
    }
  }
};

type BaseJobQueryParams<E extends JobEndpoint, Args, Response> = {
  build: EndpointBuilder<any, ApiTags, any>;
  endpointName: E;
  transformResponse?: (
    rawResponse: JobsTypes[E]["rawResponse"],
    args: Args
  ) => Response;
  getArgs?: (args: Args) => Args;
  onQueryStarted?: (arg: Args, api: any) => Promise<void> | void;
};

const buildJobQueryFn = <E extends JobEndpoint, Args, Response>(
  endpointName: E,
  getArgs: ((args: Args) => Args) | undefined,
  transformResponse:
    | ((rawResponse: JobsTypes[E]["rawResponse"], args: Args) => Response)
    | undefined
) => {
  return async (args: Args, _: any, __: any, baseQuery: any): Promise<any> => {
    const url = JOB_TYPE_ENDPOINTS_MAPPING[endpointName];
    if (!url) return;
    let finalArgs: any = getArgs ? getArgs(args) : args;
    if (!NO_JOB_ARG_JOB_TYPES.has(endpointName)) {
      if (!finalArgs) finalArgs = { run_as_job: true };
      else finalArgs = { ...finalArgs, run_as_job: true };
    }
    const getJobIdResponse = await baseQuery({
      url,
      method: "POST",
      body: finalArgs,
    });
    const jobId: string | undefined = getJobIdResponse?.data?.data?.job_id;
    if (jobId) {
      return await handleGetJobResults(
        endpointName,
        baseQuery,
        jobId,
        args,
        transformResponse
      );
    }
  };
};

export const getJobQuery = <
  E extends JobEndpoint,
  Args = JobsTypes[E]["args"],
  Response = JobsTypes[E]["response"]
>({
  build,
  endpointName,
  transformResponse,
  getArgs,
  providesTags,
  keepUnusedDataFor,
  onQueryStarted,
}: BaseJobQueryParams<E, Args, Response> & {
  providesTags?: ResultDescription<
    ApiTags,
    Response,
    Args,
    FetchBaseQueryError,
    any
  >;
  keepUnusedDataFor?: number;
}) => {
  return build.query<Response, Args>({
    queryFn: buildJobQueryFn(endpointName, getArgs, transformResponse),
    providesTags,
    keepUnusedDataFor,
    onQueryStarted,
  });
};

export const getMutationJobQuery = <
  E extends JobEndpoint,
  Args = JobsTypes[E]["args"],
  Response = JobsTypes[E]["response"]
>({
  build,
  endpointName,
  transformResponse,
  getArgs,
  invalidatesTags,
  onQueryStarted,
}: BaseJobQueryParams<E, Args, Response> & {
  invalidatesTags?: (
    result: Response | undefined,
    error: FetchBaseQueryError | undefined,
    args: Args
  ) => TagDescription<ApiTags>[];
}) => {
  return build.mutation<Response, Args>({
    queryFn: buildJobQueryFn(endpointName, getArgs, transformResponse),
    invalidatesTags,
    onQueryStarted,
  });
};
